/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { RightArrow } from '../assets/svg/ArrowIcon';
import { EventType, useAnalytics } from '../ui/Analytics';
import { useSelector } from 'react-redux';
import { RootState } from '@rb-serve-app/core';
import { useProductName } from '@rb-serve-app/core';
import { useNavigate } from 'react-router-dom';
import AnnouncementImage from '../assets/images/Announcement.jpg';

interface AnnouncementsProps {
  contentfulData: {
    announcementTitle: string;
    announcementSubtitle: string;
    announcementDescription: string;
    viewAllAnnouncementCtaTitle: string;
    viewAllAnnouncementCtaAccText: string;
    rbWelcomePage: any;
    announcementLogo: {
      sys: {
        type: string;
        linkType: string;
        id: string;
      };
    };
    annoucementLogoAltText: string;
  };

  assets: {
    announcement_logo: string;
    [key: string]: string;
  };
  featureFlaggingData: any;
  setShowFeatureFlagMessage: React.Dispatch<React.SetStateAction<boolean>>;
  onStartTutorial?: () => void;
}

const Announcements: React.FC<AnnouncementsProps> = ({
  contentfulData,
  assets,
  featureFlaggingData,
  setShowFeatureFlagMessage,
  onStartTutorial,
}) => {
  const { productName } = useProductName();
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const { trackEvent } = useAnalytics();
  const { accountDetails } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsSmallScreen(width <= 640);
      // Mobile: < 768px, Tablet/Desktop: >= 768px (includes iPad Mini 768px and larger)
      setIsMobileDevice(width < 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const rbWelcomePage = contentfulData?.rbWelcomePage;
  console.log('rbWelcomePage data', rbWelcomePage);
  // Determine which data source to use
  const isWelcomePageEnabled = rbWelcomePage?.welcomePageEnabled === true;

  // Get the announcement image
  const getAnnouncementImage = () => {
    if (
      isWelcomePageEnabled &&
      rbWelcomePage?.announcementImage &&
      assets[rbWelcomePage.announcementImage]
    ) {
      return assets[rbWelcomePage.announcementImage];
    }
    return AnnouncementImage;
  };

  // Handle CTA click
  const handleCtaClick = () => {
    trackEvent(
      EventType.CLICK_EVENT,
      'dashboard',
      window.location.href,
      'dashboard|view all announcements'
    );

    if (window.scrollY !== 0) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    if (isWelcomePageEnabled) {
      // For mobile devices, always navigate to welcome content page
      if (isMobileDevice) {
        navigate('/welcome-content');
      } else {
        // For tablet and desktop, use interactive tutorial if available
        if (onStartTutorial) {
          onStartTutorial();
        } else {
          // Fallback to welcome content page if tutorial is not available
          navigate('/welcome-content');
        }
      }
    } else {
      setShowFeatureFlagMessage(true);
      setTimeout(() => {
        setShowFeatureFlagMessage(false);
      }, 3000);
    }
  };

  return (
    <div
      className="flex flex-1 flex-col-reverse sm:flex-col-reverse md:flex-row lg:gap-3 xl:min-h-[230px] justify-between xl-mt-0 md:mt-0"
      key={
        isWelcomePageEnabled
          ? rbWelcomePage?.announcementTitle
          : contentfulData?.announcementTitle
      }
    >
      <div className="md:w-4/5 pl-5 pb-2 lg:pr-0 ipad-portrait:pr-4 ipad-portrait:pb-4 p-0">
        <h2 className="text-[16px] font-[650] mb-2 mt-5 text-black">
          {isWelcomePageEnabled
            ? rbWelcomePage?.announcementTitle
            : contentfulData?.announcementTitle}
        </h2>
        <h3 className="text-[22px] font-[650] text-black cursor-pointer group-hover:underline leading-7">
          <span
            aria-label={contentfulData?.announcementSubtitle}
            dangerouslySetInnerHTML={{
              __html:
                (isWelcomePageEnabled
                  ? rbWelcomePage?.announcementSubtitle
                  : contentfulData?.announcementSubtitle) || '',
            }}
          />{' '}
          {!isWelcomePageEnabled && (
            <span
              aria-label={productName}
              dangerouslySetInnerHTML={{
                __html: productName || '',
              }}
            />
          )}
        </h3>
        <p className="text-[16px] text-black mt-2 leading-5">
          <span
            aria-label={contentfulData?.announcementDescription?.replace(
              '%s',
              productName
            )}
            dangerouslySetInnerHTML={{
              __html:
                (isWelcomePageEnabled
                  ? rbWelcomePage?.announcementDescription
                  : contentfulData?.announcementDescription?.replace(
                      '%s',
                      productName
                    )) || '',
            }}
          />
        </p>
        <div className="text-left mt-9">
          <div className="flex group">
            <p
              aria-label={
                isWelcomePageEnabled
                  ? rbWelcomePage?.viewAllAnnouncementCTAAccText || 'Learn more'
                  : 'View all announcements'
              }
              onClick={handleCtaClick}
              className={`${isWelcomePageEnabled || featureFlaggingData ? 'text-primary-red' : 'text-gray-500'} font-[650] text-[16px] cursor-pointer`}
            >
              {isWelcomePageEnabled
                ? rbWelcomePage?.viewAllAnnouncementCTATitle || 'Learn more'
                : 'View all announcements'}
            </p>
            <span className="hidden group-hover:block ml-2 w-200px transition-opacity duration-2000 ease-in-out">
              <RightArrow
                fill={
                  isWelcomePageEnabled || featureFlaggingData ? '#fff' : '#aaa'
                }
              />
            </span>
          </div>
        </div>
      </div>

      <div
        aria-label={
          isWelcomePageEnabled
            ? rbWelcomePage?.announcementTitle
            : contentfulData?.announcementTitle
        }
        className="flex justify-end ipad-portrait:min-h-[230px] min-h-[230px] md:rounded-r-lg md:rounded-b-lg md:rounded-bl-none"
        style={{ width: isSmallScreen ? '100%' : '50%' }}
      >
        <img
          src={getAnnouncementImage()}
          alt="announcementImg"
          className="xl:max-h-[230px] w-full object-cover"
          style={{
            borderTopRightRadius: '0.5rem',
            borderTopLeftRadius: isSmallScreen ? '0.5rem' : '',
            borderBottomRightRadius: isSmallScreen ? '' : '0.5rem',
          }}
        />
      </div>
    </div>
  );
};
export default React.memo(Announcements);
